import { apiClient } from './apiClient';

/**
 * Reports crashes and uncaught errors to the backend so they land on the admin
 * errors page.
 *
 * The errors page only ever held server-side exceptions, so a screen that went
 * blank on someone's phone left no trace anywhere. Both apps install this; the
 * device fields come from a host-supplied describer because "what device is
 * this" is the one thing web and React Native genuinely cannot share.
 *
 * Platform and app build are NOT sent from here — the backend reads them from
 * the X-Client-* headers apiClient already attaches, so a report cannot claim
 * to be a different app than it is.
 */
export interface ClientErrorDevice {
  os?: string;
  os_version?: string;
  device_model?: string;
  /** "390x844" */
  screen?: string;
  locale?: string;
}

export interface ClientErrorReport {
  /** Error class or name — the grouping key on the errors page. */
  kind: string;
  message: string;
  stack?: string;
  /** Where in the app: a route, a screen name, an ErrorBoundary label. */
  where?: string;
  /** True for a crash that took the app down, false for a caught error. */
  fatal?: boolean;
}

type DeviceDescriber = () => ClientErrorDevice;

let describeDevice: DeviceDescriber = () => ({});

/** Called once at startup by each host with what it knows about the device. */
export function configureErrorReporter(describer: DeviceDescriber): void {
  describeDevice = describer;
}

/** The device fields the host registered, for any other report that needs
 *  them (the bug-report form sends the same os / os_version / device_model). */
export function describeClientDevice(): ClientErrorDevice {
  return describeDevice();
}

// A crash inside a render loop can fire the same error thousands of times.
// Reporting each one would flood the log with a single bug and hit the server
// throttle, losing everything else — so identical reports are sent once per
// window, and the count is not worth the complexity of tracking.
const RECENT_WINDOW_MS = 30_000;
const recentlySent = new Map<string, number>();

function isDuplicate(key: string, now: number): boolean {
  const last = recentlySent.get(key);
  if (last !== undefined && now - last < RECENT_WINDOW_MS) return true;
  recentlySent.set(key, now);
  // Bounded: drop anything outside the window rather than growing forever.
  for (const [k, t] of recentlySent) {
    if (now - t >= RECENT_WINDOW_MS) recentlySent.delete(k);
  }
  return false;
}

/**
 * Fire-and-forget. Never throws and never rejects: an error reporter that can
 * itself fail loudly turns one bug into two.
 */
export function reportClientError(report: ClientErrorReport): void {
  try {
    const key = `${report.kind}|${report.message}`.slice(0, 200);
    if (isDuplicate(key, Date.now())) return;

    apiClient.post('/api/v1/client_errors', {
      kind:    report.kind,
      message: report.message,
      stack:   report.stack,
      where:   report.where,
      fatal:   report.fatal ?? false,
      // See the lookup-event note: this can be replayed from the offline outbox
      // long after the crash, and a crash timestamped at reconnect is useless
      // for correlating with what the reader was doing.
      occurred_at: new Date().toISOString(),
      ...describeDevice(),
    }).catch(() => {});
  } catch {
    // Reporting is best-effort by definition.
  }
}

/** Normalises whatever was thrown — JS lets you throw anything. */
export function describeThrown(error: unknown): { kind: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { kind: error.name || 'Error', message: error.message, stack: error.stack };
  }
  return { kind: 'Error', message: String(error) };
}

